import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import JsPDF from "jspdf";
import "jspdf-autotable";
import SkeletonTicketList from "../../../loaders/SkeletonTicketList";
import { DeleteLtiIntegration, GetLtiIntegrations } from "../../../services/LtiIntegrationService";
import { handleTableScroll } from "../../../utils/commonFunction";
import { TABLE_ROWS_PER_PAGE } from "../../../utils/Constants";
import Str from "../../common/Str";

/**
 * Lists LTI integrations with server-side pagination and export.
 */
function IntegrationTable() {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState("id");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [deleteIntegration, setDeleteIntegration] = useState(false);
  useEffect(() => {
    handleTableScroll();
  }, [loading]);

  useEffect(() => {
    const cancelTokenSources = [];

    /**
     * Fetches paginated integrations from backend.
     */
    const fetchIntegrations = async () => {
      setLoading(true);

      cancelTokenSources.forEach((source) => {
        source.cancel("New request made");
      });

      const source = axios.CancelToken.source();
      cancelTokenSources.push(source);

      const payload = {
        page,
        limit: perPage,
        sortKey,
        sortOrder,
        search,
        exportStatus: "false",
      };

      try {
        const response = await GetLtiIntegrations(payload, source.token);
        if (response.status === 200) {
          setIntegrations(response?.data?.integrations?.data || []);
          setTotalRows(response?.data?.integrations?.total || 0);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchIntegrations();

    return () => {
      cancelTokenSources.forEach((source) => {
        source.cancel("Component unmounted");
      });
    };
  }, [page, perPage, sortKey, sortOrder, search, deleteIntegration]);

  /**
   * Updates current page number.
   */
  const handlePageChange = (pageNo) => {
    setPage(pageNo);
  };

  /**
   * Updates rows-per-page value.
   */
  const handlePerRowsChange = (newPerPage) => {
    setPerPage(newPerPage);
  };

  /**
   * Updates sort column and direction from DataTable.
   */
  const handleSort = (column, sortDirection) => {
    setSortKey(column.sortField);
    setSortOrder(sortDirection === "asc" ? "ASC" : "DESC");
  };

  /**
   * Updates search filter value and resets to first page.
   */
  const handleSearchFilter = (event) => {
    setPage(1);
    setSearch(event.target.value);
  };

  /**
   * Clears table search filter.
   */
  const resetFilter = () => {
    setPage(1);
    setSearch("");
  };

  /**
   * Exports filtered integrations using backend full list (exportStatus true).
   */
  const exportData = (fileType, fileName) => {
    const header = ["Name", "Client ID", "Service Endpoint"];

    Swal.fire({
      title: "File downloading",
      onOpen: function () {
        Swal.showLoading();
      },
    });

    const payload = {
      page,
      limit: perPage,
      sortKey,
      sortOrder,
      search,
      exportStatus: "true",
    };

    GetLtiIntegrations(payload)
      .then((response) => {
        const integrationsList = response?.data?.integrations;
        let data = Array.isArray(integrationsList)
          ? integrationsList
          : integrationsList?.data || [];

        data = data?.map((row) => ({
          ...row,
          Name: row.integration_name ? row.integration_name : "-",
          "Client ID": row.client_id ? row.client_id : "-",
          "Service Endpoint": row.service_endpoint ? row.service_endpoint : "-",
        }));

        if (fileType === "csv") {
          const csvString = Papa.unparse({ fields: header, data });
          const blob = new Blob([csvString], { type: "text/csv;charset=utf-8," });
          const blobURL = window.URL.createObjectURL(blob);
          const anchor = document.createElement("a");
          anchor.download = fileName;
          anchor.href = blobURL;
          anchor.click();
          setTimeout(() => URL.revokeObjectURL(blobURL), 1000);
          Swal.close();
        } else if (fileType === "xlsx") {
          const compatibleData = data.map((row) => {
            const obj = {};
            header.forEach((col) => {
              obj[col] = row[col];
            });
            return obj;
          });
          const wb = XLSX.utils.book_new();
          const ws1 = XLSX.utils.json_to_sheet(compatibleData, { header });
          XLSX.utils.book_append_sheet(wb, ws1, "React Table Data");
          XLSX.writeFile(wb, `${fileName}.xlsx`);
          Swal.close();
          return false;
        }

        if (fileType === "pdf") {
          const compatibleData = data.map((row) => {
            return [row.Name, row["Client ID"], row["Service Endpoint"]];
          });
          const doc = new JsPDF();
          doc.autoTable({
            head: [header],
            body: compatibleData,
            styles: {
              minCellHeight: 10,
              minCellWidth: 5,
              halign: "left",
              fontSize: 8,
            },
          });
          doc.save(`${fileName}.pdf`);
          Swal.close();
          return false;
        }
      })
      .catch((error) => {
        Swal.close();
        const payloadError = error?.response?.data;
        const message = Array.isArray(payloadError)
          ? payloadError.map((item) => item?.message).filter(Boolean).join("\n")
          : payloadError?.message;

        Swal.fire({
          icon: "error",
          title: "Error",
          text: message || "Something Went Wrong!.",
        });
      });
  };

  /**
   * Deletes integration.
   */
  const deleteIntegrationAction = (id) => {
      Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      }).then((result) => {
        if (result.isConfirmed) {
          DeleteLtiIntegration({integrationId:id}).then((response) => {
            if (response.status === 200) {
              Swal.fire({
                icon: "success",
                title: "Success",
                text: "Integration deleted successfully!",
              });
              setDeleteIntegration(true);
            }
          });
          }
        })
        .catch((error) => {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: error?.response?.data?.message || "Something Went Wrong!.",
          });
        })
  }

  const columns = useMemo(
    () => [
      {
        name: "Name",
        selector: "integration_name",
        sortField: "integration_name",
        sortable: true,
        cell: (row) => (
          <Link
            title={row.integration_name}
            className="as-text-blue curser feature-name"
            to={`/administration/integrations/open/${row.id}/details`}
          >
            <span className="textLimit100">{row.integration_name}</span>
          </Link>
        ),
      },
      {
        name: "Client ID",
        selector: "client_id",
        sortField: "client_id",
        sortable: true,
        cell: (row) => (
          <span className="feature-name" title={row.client_id}>
            <span className="textLimit100">{row.client_id}</span>
          </span>
        ),
      },
      {
        name: "Service Endpoint",
        selector: "service_endpoint",
        sortField: "service_endpoint",
        sortable: true,
        cell: (row) => (
          <span className="feature-name" title={row.service_endpoint}>
            <span className="textLimit100">{row.service_endpoint}</span>
          </span>
        ),
      },
      {
        name: "Action",
        cell: (row) => (
          <div className="assessment-08">
            <div className="as-buttons">
              <Link
                className="btn btn-primary rounded-circle"
                to={`/administration/integrations/open/${row.id}/details`}
                title="Open"
              >
                <i className="fal fa-folder-open"></i>
              </Link>
              <button
                className="btn btn-danger rounded-circle"
                onClick={() => deleteIntegrationAction(row.id)}
                title="Delete"
              >
                <i className="fal fa-trash-alt"></i>
              </button>
            </div>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <div className="my-tickets-info-list Tickets-main-wrap">
      <div className="custom-table-div filter-search-icon card card-table-custom">
        <div className="search-filter-div">
          <div className="search-filter-div-left">
            <div className="system-administration-table table-responsive">
              <div className="table-responsive-div">
                <div id="assessment-table-main_wrapper" className="dataTables_wrapper no-footer">
                  <div id="assessment-table-main_filter" className="dataTables_filter">
                    <label>
                      <input
                        type="search"
                        placeholder="Search"
                        aria-controls="assessment-table-main"
                        onChange={handleSearchFilter}
                        value={search}
                      />
                    </label>
                    <div className="filter-eff filter-data-btn">
                      <button className="filter-buttons" type="button">
                        <i className="fal fa-filter"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="filter-button-group">
                <div className="reset-btn-group">
                  <div className="button-reset dropdown-comman">
                    <button className="btn btn-primary" onClick={resetFilter} title="Reset" type="button">
                      <i className="fal fa-redo"></i>Reset
                    </button>
                  </div>
                  <div className="files-export-group">
                    <button
                      type="button"
                      className="btn btn-files"
                      onClick={() => {
                        exportData("xlsx", "LTI_Integrations");
                      }}
                      title="Export spreadsheet"
                    >
                      <i className="fal fa-file-excel icon"></i>
                    </button>
                    <button
                      type="button"
                      className="btn btn-files"
                      onClick={() => {
                        exportData("csv", "LTI_Integrations");
                      }}
                      title="Export CSV"
                    >
                      <i className="fal fa-file-csv icon"></i>
                    </button>
                    <button
                      type="button"
                      className="btn btn-files"
                      onClick={() => {
                        exportData("pdf", "LTI_Integrations");
                      }}
                      title="Export PDF"
                    >
                      <i className="fal fa-file-pdf icon"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="search-filter-div-right">
            <div className="filter-search-bar-blk">
              <div className="add-ticket-blk button-reset dropdown-comman">
                <Link to="/administration/integrations/create" className="btn btn-primary" title="Create New">
                  <i className="fal fa-plus"></i>Create New
                </Link>
              </div>
            </div>
          </div>
        </div>

        <DataTable
          paginationDefaultPage={page}
          data={integrations}
          columns={columns}
          defaultSortField="id"
          defaultSortAsc={false}
          paginationServer
          pagination
          paginationTotalRows={totalRows}
          progressPending={loading}
          progressComponent={<SkeletonTicketList />}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          onSort={handleSort}
          highlightOnHover={false}
          noDataComponent={Str.noRecord}
          paginationRowsPerPageOptions={TABLE_ROWS_PER_PAGE}
        />
      </div>
    </div>
  );
}

export default IntegrationTable;
